import React, { useState, useCallback, useEffect } from 'react';
import { Banner } from '../../../../common/Banner';
import { PageLoader } from '../../../../loader';
import { PeriodSelector } from '../../../../common/PeriodsSelector';
import { useAvailableYears } from '../../../../../hooks/useAvailableYears';
import { usePeriodLogic } from '../../../../../hooks/usePeriodLogic';
import { getSocialQuotasSend } from '../../../../../services/quotasService';
import QuotasError from './QuotasError';
import QuotasUnconfirmed from './QuotasUnconfirmed';

const SendQuotas = () => {
  const [formState, setFormState] = useState({
    selectedYear: '',
    reportType: '', 
    selectedPeriod: ''
  });
  const [response, setResponse] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);
  const { availableYears, isLoading, error: yearsError } = useAvailableYears();
  const { getApiPeriodName } = usePeriodLogic();

  // Manejar cambios del selector
  const handleFormChange = useCallback((field, value) => {
    setFormState(prev => {
      const newState = { ...prev, [field]: value };
      
      if (field === 'selectedYear') {
        newState.reportType = '';
        newState.selectedPeriod = '';
      } else if (field === 'reportType') {
        newState.selectedPeriod = '';
      }
      
      return newState;
    });
    setResponse(null);
    setError(null); 
  }, []);


  const loadData = useCallback(async () => {
    const { selectedYear, reportType, selectedPeriod } = formState;
    if (selectedYear && selectedPeriod && reportType) {
      setLoadingData(true);
      setError(null);
      const periodName = getApiPeriodName(reportType, selectedPeriod);
      try {
        const response = await getSocialQuotasSend(selectedYear, periodName);
        // Verificar si la respuesta contiene un error
        if (response && (response.statusCode >= 400 || response.error)) {
          setResponse(null);
          setError("Error al obtener las cuotas sociales. Por favor, intente nuevamente más tarde.");
        } else {
          setResponse(response);
        }
      } catch (error) {
        console.error("Error fetching social quotas:", error);
        setError("Error al obtener las cuotas sociales. Por favor, intente nuevamente más tarde.");
        setResponse(null);
      } finally {
        setLoadingData(false);
      }
    } else {
      setResponse(null);
      setLoadingData(false);
    }
  }, [formState, getApiPeriodName]);
  
  const reload = useCallback(() => {
    setResponse(null);
    setError(null);
    loadData();
  }, [loadData]);

  // Simular carga de datos cuando el formulario está completo
  useEffect(() => {
    const { selectedYear, selectedPeriod, reportType } = formState;

    if (selectedYear && selectedPeriod && reportType) {
      loadData();
    }
  }, [formState, getApiPeriodName]);

  if (isLoading) {
    return <PageLoader message="Cargando reportes..." />;
  }

  const isFormComplete = formState.selectedYear && formState.reportType && formState.selectedPeriod;

  return (
    <div className="w-full space-y-6">
      <Banner
        title="Aqui puede enviar las cuotas sociales"
        message="Seleccione el año, tipo de reporte y periodo para enviar las cuotas sociales correspondientes."
      />

      <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
        <PeriodSelector
          availableYears={availableYears}
          selectedYear={formState.selectedYear}
          reportType={formState.reportType}
          selectedPeriod={formState.selectedPeriod}
          onYearChange={(year) => handleFormChange('selectedYear', year)}
          onReportTypeChange={(type) => handleFormChange('reportType', type)}
          onPeriodChange={(period) => handleFormChange('selectedPeriod', period)}
          disabled={loadingData}
        />

        {loadingData && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm text-blue-800">Cargando datos...</span>
            </div>
          </div>
        )}

        {isFormComplete && !loadingData && response && !error && (
          <div className="mt-6 bg-gray-50 rounded-lg p-4">
            {response.errorGenerating ? (
              <QuotasError period={formState.selectedPeriod} year={formState.selectedYear} />
            ) : ( response.quotas && response.quotas.length > 0 && !response.confirmed ? (
              <QuotasUnconfirmed quotas={response.quotas} ufUsed={response.ufUsed} period={getApiPeriodName(formState.reportType,formState.selectedPeriod)} year={formState.selectedYear} reload={reload} />
            ) : (
              <div className="text-center space-y-4">
                <h4 className="font-semibold">Cuotas confirmadas</h4>
                <p>Las cuotas han sido generadas y confirmadas. Por favor, dirígete a la sección de reportes para ver el detalle.</p>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <span className="text-red-600">{error}</span>
            </div>
            <div className="mt-2">
              <span className="text-sm text-red-800">Hubo un error al obtener las cuotas sociales. Por favor, intente nuevamente más tarde.</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SendQuotas;